import { DisplayTypes } from "@/config";
import { Column, Comparison, DataPoint } from "@/types";

// MEMBER

export function formatWithMonthNumber(
  start_date: string,
  months_per_column: number
): string {
  // Quarter and Semester cases have offsets because getMonth is 0 indexed whereas they are 1 indexed.
  const asDate = new Date(start_date);

  if (months_per_column === 12) {
    // Return year when start date is January 1st
    if (asDate.getMonth() === 0 && asDate.getDate() === 1) {
      return `${asDate.getFullYear()}`;
    }
    return `${asDate.getFullYear()} - ${asDate.getFullYear() + 1}`;
  } else if (months_per_column === 6) {
    const semester = 1 + asDate.getMonth() <= 6 ? 1 : 2;
    return `S${semester} - ${asDate.getFullYear()}`;
  } else if (months_per_column === 3) {
    const quarter = 1 + Math.floor(asDate.getMonth() / 3);
    return `Q${quarter} - ${asDate.getFullYear()}`;
  } else if (months_per_column === 1) {
    return start_date;
  } else {
    return start_date;
  }
}

// COLLECTION

export function extractHeaders(data: Comparison): string[] {
  return data.columns.sort(byDate).map((column: Column) => {
    return formatWithMonthNumber(column.start_date, data.months_per_column);
  });
}

export function extractRows(columns: Column[], type: DisplayTypes) {
  return columns.sort(byDate).map((column: Column) => {
    return (column.data_points || [])
      .filter((data_point: DataPoint) => data_point.view === type)
      .sort(byName);
  });
}

export function extractSeries(
  columns: Column[],
  type: DisplayTypes
): Array<Array<DataPoint>> {
  return rotateRows(extractRows(columns, type), true);
}

export function extractStartYear(data: Comparison): number | undefined {
  const startDate = data.columns.sort(byDate).at(0)?.start_date;
  if (startDate) {
    const dateStartDate = new Date(startDate);
    return dateStartDate.getFullYear();
  }
  return undefined;
}

export function extractEndYear(data: Comparison): number | undefined {
  const endDate = data.columns.sort(byDate).at(-1)?.start_date;
  if (endDate) {
    const dateEndDate = new Date(endDate);
    return dateEndDate.getFullYear();
  }
  return undefined;
}

const SORT = [
  "usage",
  "operation",
  "support",
  "office",
  "transport",
  "other",
  "scope_1",
  "scope_2",
  "scope_3",
];

export function byDate(a: Column, b: Column): number {
  return new Date(a.start_date).getTime() - new Date(b.start_date).getTime();
}

export function byName(a: DataPoint, b: DataPoint): number {
  return SORT.indexOf(a.key) - SORT.indexOf(b.key);
}

// This function was added to make the current comparison format
// easier to work with. This might show that this current format
// is not ideal.
// https://medium.com/swlh/matrix-rotation-in-javascript-269cae14a124
function rotateRows(source: DataPoint[][], counter = false) {
  const M = source.length;
  const N = source[0].length;

  const destination = new Array(N);
  for (let i = 0; i < N; i++) {
    destination[i] = new Array(M);
  }

  for (let i = 0; i < N; i++) {
    for (let j = 0; j < M; j++) {
      if (counter) {
        destination[i][j] = source[j][N - i - 1];
      } else {
        destination[i][j] = source[M - j - 1][i];
      }
    }
  }

  return destination;
}

function columnHasScopes(column: Column): boolean {
  return column.data_points!.some(
    (data_point) => data_point.view === DisplayTypes.byScope
  );
}

export function getScopesAvailability(comparisonData: Comparison): boolean {
  return comparisonData.columns.every((column) => columnHasScopes(column));
}
